import type {ReactElement} from 'react';
import React, {useEffect, useRef, useState} from 'react';
import {Dropdown} from '@Components/dropdown-v2';
import {getNoOfGridItems} from '@Components/base-grid';
import {setHasLanguageSelectorJustClosed} from '@Panels/ai-subtitle-panel/ai-subtitle-reducer';
import type {AddAiItemButtonProps} from './add-ai-item-button.types';
import {AddAiItemPopOverContentState, AI_IMAGE_PANEL_ID, AI_SUBTITLE_PANEL_ID, AI_VOICE_PANEL_ID} from './add-ai-item-button.types';
import styles from './add-ai-item-button.module.scss';
import {useAppDispatch, useAppSelector} from '@/hooks';
import {ALIGNMENT_TYPE, DROPDOWN_POSITION} from '@Components/dropdown-v2/dropdown.types';
import {GA4EventName, GA4EventParamName, GA4EventSources, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import {AiSubtitleMenuOpenedFrom, getAiSubtitlesPanelHeightClass} from '@Libraries/ai-transcript.library';
import {getUserId, isUserPremium} from '@Libraries/user.library';
import {AddAiItemDropdownPopupContent} from '@Components/add-ai-item-button/components/add-ai-item-dropdown-popup-content/add-ai-item-dropdown-popup-content';
import {AddItemButton} from '@Components/poster-editor/components/poster-add-item-bar/components/add-item-button';
import {getGridIdForPanel} from '@Libraries/text-to-image-library';
import {AiVoicePanelDataKey, AiVoicePanelGenerationState, AiVoicePanelHeightChangingEvents} from '@Panels/ai-voice-panel/ai-voice-panel.types';
import {showLoading} from '@Libraries/loading-toast-library';
import {getLoadingKeyForAiVoice} from '@Panels/ai-voice-panel/ai-voice-panel.library';
import {AddAiItemPopUpSubPanel} from '@Components/add-ai-item-button/components/add-ai-item-dropdown-popup-content/add-ai-item-dropdown-popup-content.types';
import {getHasUserSeenAiVoice, markUserHasSeenAiVoice} from '@Libraries/text-to-speech.library';
import {ADD_ITEM_BAR_POPUP_OFFSET, PosterAddItemBarHeightThreshold} from '@Components/poster-editor/components/poster-add-item-bar/poster-add-item-bar.types';
import {FeatureToShowOnLoad} from '@Components/poster-editor/poster-editor.types';
import {updateRemoveBgMode} from '@Components/poster-editor/poster-editor-reducer';
import {GROWL_TYPE, showMessageGrowl} from '@Components/message-growl';
import type {CanvasEvents} from '@postermywall/fabricjs-2';
import {changeCanvasCursor} from '@Components/poster-editor/library/poster-editor-library';

const MIN_ITEMS_IN_LEFT_BAR = 6;
const ADD_ITEM_BAR_ITEM_WIDTH = 58;
const ADD_ITEM_BAR_ITEM_SPACING = 12;
const ADD_ITEM_BAR_SPACING = 18;

export function AddAiItemButton({arrowClasses = '', popupClassName = '', ...props}: AddAiItemButtonProps): ReactElement<any> {
  const dispatch = useAppDispatch();
  const isUserLoggedIn = useRef<boolean>(typeof getUserId() !== 'undefined');
  const isPremium = useRef(isUserLoggedIn.current && isUserPremium());
  const [showAiVoiceAsNewFeature, setShowAiVoiceAsNewFeature] = useState<boolean>(false);
  const [isOpened, setIsOpened] = useState(false);
  const [dropdownContentState, setDropdownContentState] = useState(AddAiItemPopOverContentState.AI_ITEMS_DROPDOWN);
  const numberOfGridItemsInAiImageGrid = useAppSelector((state) => {
    return getNoOfGridItems(state, getGridIdForPanel(AI_IMAGE_PANEL_ID));
  });
  const aiSubtitleReduxState = useAppSelector((state) => {
    return state.aiSubtitle;
  });
  const aiSubtitleState = useAppSelector((state) => {
    return state.posterEditor.aiState.aiSubtitles;
  });
  const hasUserMediaPanelJustClosed = useAppSelector((state) => {
    return state.panels.panelHashmap['user-media-modal-panel'];
  });
  const hasLanguageSelectorJustClosed = useAppSelector((state) => {
    return state.aiSubtitle.aiSubtitlesPanel.hasLanguageSelectorJustClosed;
  });
  const aiVoicePanelHeightState = useAppSelector((state): undefined | AiVoicePanelHeightChangingEvents => {
    return state.panels.panelHashmap[AI_VOICE_PANEL_ID]?.data?.[AiVoicePanelDataKey.STATE] as AiVoicePanelHeightChangingEvents;
  });
  const aiVoicePanelGenerationState = useAppSelector((state): AiVoicePanelGenerationState | undefined => {
    return state.panels.panelHashmap[AI_VOICE_PANEL_ID]?.data?.[AiVoicePanelDataKey.GENERATION_STATE] as AiVoicePanelGenerationState;
  });
  const featureToShowOnLoad = useAppSelector((state) => {
    return state.posterEditor.featureToShowOnLoad;
  });

  useEffect(() => {
    if (featureToShowOnLoad === FeatureToShowOnLoad.AI_SUBTITLE) {
      setIsOpened(true);
      setDropdownContentState(AddAiItemPopOverContentState.AI_SUBTITLES);
    } else if (featureToShowOnLoad === FeatureToShowOnLoad.AI_VOICE) {
      setIsOpened(true);
      setDropdownContentState(AddAiItemPopOverContentState.AI_VOICE);
    } else if (featureToShowOnLoad === FeatureToShowOnLoad.AI_IMAGE) {
      setIsOpened(true);
      setDropdownContentState(AddAiItemPopOverContentState.AI_IMAGE);
    } else if (featureToShowOnLoad === FeatureToShowOnLoad.AI_REMOVE_BG) {
      setIsOpened(true);
    }
  }, [featureToShowOnLoad]);

  const getAIOptionsHeight = (): number => {
    let numItems;
    if (props.heightThreshold === PosterAddItemBarHeightThreshold.XXXSMALL) {
      numItems = MIN_ITEMS_IN_LEFT_BAR;
    } else if (props.heightThreshold === PosterAddItemBarHeightThreshold.XXSMALL) {
      numItems = MIN_ITEMS_IN_LEFT_BAR + 1;
    } else if (props.heightThreshold === PosterAddItemBarHeightThreshold.XSMALL) {
      numItems = MIN_ITEMS_IN_LEFT_BAR + 2;
    } else if (props.heightThreshold === PosterAddItemBarHeightThreshold.SMALL) {
      numItems = MIN_ITEMS_IN_LEFT_BAR + 3;
    } else if (props.heightThreshold === PosterAddItemBarHeightThreshold.MEDIUM) {
      numItems = MIN_ITEMS_IN_LEFT_BAR + 4;
    } else {
      numItems = MIN_ITEMS_IN_LEFT_BAR + 5;
    }
    return ADD_ITEM_BAR_ITEM_WIDTH * numItems + (numItems - 1) * ADD_ITEM_BAR_ITEM_SPACING + ADD_ITEM_BAR_SPACING;
  };

  const getPopupTopOffset = (): number => {
    if (!isFullHeightPopup()) {
      return 0;
    }
    if (props.heightThreshold === PosterAddItemBarHeightThreshold.XXXSMALL) {
      return -90;
    } else if (props.heightThreshold === PosterAddItemBarHeightThreshold.XXSMALL) {
      return -73;
    } else if (props.heightThreshold === PosterAddItemBarHeightThreshold.XSMALL) {
      return -38;
    } else if (props.heightThreshold === PosterAddItemBarHeightThreshold.SMALL) {
      return -4;
    } else if (props.heightThreshold === PosterAddItemBarHeightThreshold.MEDIUM) {
      return 30;
    } else {
      return 65;
    }
  }

  const getArrowTopOffset = (): number => {
    if (!isFullHeightPopup()) {
      return 0;
    }
    if (props.heightThreshold === PosterAddItemBarHeightThreshold.XXXSMALL) {
      return 0;
    } else if (props.heightThreshold === PosterAddItemBarHeightThreshold.XXSMALL) {
      return 162;
    } else if (props.heightThreshold === PosterAddItemBarHeightThreshold.XSMALL) {
      return 197;
    } else if (props.heightThreshold === PosterAddItemBarHeightThreshold.SMALL) {
      return 233;
    } else if (props.heightThreshold === PosterAddItemBarHeightThreshold.MEDIUM) {
      return 268;
    } else {
      return 305;
    }
  }

  const getSelector = (): ReactElement<any> => {
    return (
      <AddItemButton
        boldText
        isDisabled={false}
        isSelected={isOpened}
        id="toolbar-ai-images"
        text={window.i18next.t('pmwjs_ai')}
        icon="icon-ai"
        className={isOpened ? styles.selectedButton : undefined}
        textClasses={styles.selectorText}
        iconClasses={styles.selectorIcon}
      />
    );
  };

  const getNewFeatureMarkers = (): AddAiItemPopUpSubPanel[] => {
    const newFeatureMarkers: AddAiItemPopUpSubPanel[] = [];
    if (showAiVoiceAsNewFeature) {
      newFeatureMarkers.push(AddAiItemPopUpSubPanel.AI_VOICE);
    }
    return newFeatureMarkers;
  };

  const onSubPanelBack = (): void => {
    switch (dropdownContentState) {
      case AddAiItemPopOverContentState.AI_IMAGE:
      case AddAiItemPopOverContentState.AI_SUBTITLES:
      case AddAiItemPopOverContentState.AI_VOICE:
        setDropdownContentState(AddAiItemPopOverContentState.AI_ITEMS_DROPDOWN);
        break;
      default:
        break;
    }
  };

  const onDone = (): void => {
    setIsOpened(false);
    setDropdownContentState(AddAiItemPopOverContentState.AI_ITEMS_DROPDOWN);
  };

  const onClickAiSubtitlesOption = (): void => {
    trackPosterBuilderGA4Events(GA4EventName.AI_SUBTITLE_CLICK_MENU);
    setDropdownContentState(AddAiItemPopOverContentState.AI_SUBTITLES);
  };

  const onClickAiImagesOption = (): void => {
    trackPosterBuilderGA4Events(GA4EventName.AI_IMAGE_MENU_OPEN, {
      [GA4EventParamName.MENU_OPENED_FROM]: AiSubtitleMenuOpenedFrom.ADD_AI_OPTION,
      [GA4EventParamName.SOURCE]: 'PosterAddItemBar',
    });
    setDropdownContentState(AddAiItemPopOverContentState.AI_IMAGE);
  };

  const onClickAiVoiceOption = (): void => {
    void markUserHasSeenAiVoice();
    setShowAiVoiceAsNewFeature(false);
    setDropdownContentState(AddAiItemPopOverContentState.AI_VOICE);
  };

  const unselectItems = (): void => {
    window.posterEditor?.whiteboard?.clearSelection();
  };

  const onRemoveBgOption = (event?: CanvasEvents['mouse:move'] | MouseEvent | KeyboardEvent): void => {
    if (!event?.target) {
      showMessageGrowl({
        text: window.i18next.t('pmwjs_select_image_or_video_to_remove_bg'),
        type: GROWL_TYPE.NEUTRAL,
      });
    }
    dispatch(updateRemoveBgMode(false));
    const canvas = window.posterEditor?.whiteboard?.getCurrentPage().fabricCanvas;
    canvas?.off('mouse:down:before', unselectItems);
  };

  const onClickAiRemoveBgOption = (): void => {
    const items = window.posterEditor?.whiteboard?.getCurrentPage().items;
    if (items?.getItems().length) {
      const canvas = window.posterEditor?.whiteboard?.getCurrentPage().fabricCanvas;
      canvas?.on('mouse:down:before', unselectItems);
      changeCanvasCursor('images/postermaker/remove-bg.png', onRemoveBgOption);
      window.PMW.redux.store.dispatch(updateRemoveBgMode(true));
      trackPosterBuilderGA4Events(GA4EventName.REMOVE_BACKGROUND, {[GA4EventParamName.SOURCE]: GA4EventSources.LEFT_TOOLBAR});
    } else {
      showMessageGrowl({
        text: window.i18next.t('pmwjs_add_image_or_video_to_remove_bg'),
        type: GROWL_TYPE.NEUTRAL,
      });
    }
    setIsOpened(false);
  };

  const getPopupContent = (): ReactElement<any> => {
    return (
      <AddAiItemDropdownPopupContent
        showAsNewFeature={getNewFeatureMarkers()}
        dropdownContentState={dropdownContentState}
        onSubPanelBack={onSubPanelBack}
        onDone={onDone}
        onClickAiSubtitlesOption={onClickAiSubtitlesOption}
        onClickAiImagesOption={onClickAiImagesOption}
        onClickAiVoiceOption={onClickAiVoiceOption}
        onClickAiRemoveBgOption={onClickAiRemoveBgOption}
        AiImagePanelId={AI_IMAGE_PANEL_ID}
        AiVoicePanelId={AI_VOICE_PANEL_ID}
        AiSubtitlePanelId={AI_SUBTITLE_PANEL_ID}
      />
    );
  };

  const getHeightClassForAiVoice = (): string => {
    switch (aiVoicePanelHeightState) {
      case AiVoicePanelHeightChangingEvents.GENERATING:
      case AiVoicePanelHeightChangingEvents.GENERATED_RESULT:
        return styles.aiVoiceContainerLoadingStyles;
      case AiVoicePanelHeightChangingEvents.EMPTY_RESULT:
        return styles.aiVoiceContainerEmptyResultsStyles;
      case AiVoicePanelHeightChangingEvents.DEFAULT:
      default:
        return '';
    }
  };

  const getClassesForPopup = (): string => {
    switch (dropdownContentState) {
      case AddAiItemPopOverContentState.AI_ITEMS_DROPDOWN:
        return styles.aiPopup;
      case AddAiItemPopOverContentState.AI_IMAGE:
        return `${styles.aiImageContainerStyles} ${!numberOfGridItemsInAiImageGrid ? styles.emptyAiImageGridContent : ''}`;
      case AddAiItemPopOverContentState.AI_SUBTITLES:
        return `${styles.aiSubtitlesPanelPopupContainerStyles} ${getAiSubtitlesPanelHeightClass(aiSubtitleState, Object.values(aiSubtitleReduxState.aiSubtitlesPanel.items).length, aiSubtitleReduxState.generationsBatch.totalItemsToGenerateSubtitlesFor, !isPremium.current)}`;
      case AddAiItemPopOverContentState.AI_VOICE:
        return `${styles.aiVoiceContainerStyles} ${getHeightClassForAiVoice()}`;
      default:
        return '';
    }
  };

  const isFullHeightPopup = (): boolean => {
    return isUserPremium() && dropdownContentState === AddAiItemPopOverContentState.AI_IMAGE;
  };

  const shouldCloseOnOuterClick = (): boolean => {
    if (hasLanguageSelectorJustClosed) {
      setTimeout(() => {
        dispatch(setHasLanguageSelectorJustClosed(false));
      }, 10);

      return false;
    }

    return hasUserMediaPanelJustClosed === undefined;
  };

  const onAiVoicePopoverClose = (): void => {
    if (aiVoicePanelGenerationState === AiVoicePanelGenerationState.GENERATING) {
      showLoading(getLoadingKeyForAiVoice(AI_VOICE_PANEL_ID), {text: window.i18next.t('pmwjs_generating_audio')});
    }
  };

  const triggerCloseForContentState = (): void => {
    switch (dropdownContentState) {
      case AddAiItemPopOverContentState.AI_VOICE:
        onAiVoicePopoverClose();
        break;
      default:
        break;
    }
  };

  const onClose = (): void => {
    if (isOpened) {
      triggerCloseForContentState();
      setIsOpened(false);
    }
  };

  const initAiVoiceNewStatus = async (): Promise<void> => {
    try {
      const response = await getHasUserSeenAiVoice();
      setShowAiVoiceAsNewFeature(!response);
    } catch {
      setShowAiVoiceAsNewFeature(false);
    }
  };

  const init = (): void => {
    void initAiVoiceNewStatus();
  };

  useEffect(() => {
    init();
  }, []);

  return (
    <Dropdown
      doesCloseOnOuterClick={shouldCloseOnOuterClick()}
      open={isOpened}
      doCloseOnInnerClick={false}
      className="js-toolbar-text"
      id="add-ai-item-pop-over"
      key="toolbar-add-ai-item-pop-over"
      data-testid={props['data-testid']}
      alignment={ALIGNMENT_TYPE.RIGHT}
      selector={getSelector()}
      popup={getPopupContent()}
      position={DROPDOWN_POSITION.FIXED}
      popupOffset={ADD_ITEM_BAR_POPUP_OFFSET}
      topOffset={getPopupTopOffset()}
      arrowOffset={getArrowTopOffset()}
      popUpHasCustomHeight
      takeMaxHeight={isFullHeightPopup()}
      maxHeight={getAIOptionsHeight()}
      popUpHasCustomWidth
      useDefaultArrow
      popupClassName={`${popupClassName} ${styles.popoverBaseStyle} ${getClassesForPopup()}`}
      popupSpacingClass={dropdownContentState !== AddAiItemPopOverContentState.AI_ITEMS_DROPDOWN ? 'spacing-p-0' : ''}
      arrowClasses={arrowClasses}
      onOpen={(): void => {
        if (!isOpened) {
          setIsOpened(true);
        }
      }}
      onClose={onClose}
      isResumable
    />
  );
}
